import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { erpTableRowClasses } from '@euroasiann/shared';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { canEditAdminOffer } from '../../utils/admin-offer-edit';
import { useToast } from '../../components/shared/Toast';
import { authenticatedFetch } from '../../lib/api';
import { displayCurrencyMark } from '../../lib/currency';
import { CurrencyPicker } from '../../components/shared/CurrencyPicker';
import { cn } from '../../lib/utils';
import { MdDelete, MdDeleteOutline, MdAdd, MdCheckCircle, MdClose, MdDownload, MdArrowForward, MdInsertDriveFile, MdFileUpload } from 'react-icons/md';
import { downloadAdminOfferPDF, buildAdminOfferPdfBlob, adminOfferPdfBlobToBase64 } from '../../utils/offer-pdf';
import { downloadRfqExcelTemplate } from '../../utils/rfq-excel-template';
import { parseRfqExcelFile } from '../../utils/rfq-excel-import';
import { RfqExcelImportPreview } from '../../utils/rfq-excel.types';
import { OfferExcelImportModal } from './components/OfferExcelImportModal';
import { OfferPdfPreviewSection } from './components/OfferPdfPreviewSection';
import { OFFER_SALE_TERMS_ROUTE } from '../../constants/offer-sale-terms';
import type { AdminOfferLike } from '../../utils/offer-pdf';
import {
  VesselNameCombobox,
  getVesselDisplayName,
  isRegistryVesselSelection,
} from './components/VesselNameCombobox';
import { DepartmentCombobox } from './components/DepartmentCombobox';
import { OfferCrmSuggestField, type OfferCrmContact } from './components/OfferCrmSuggestField';
import { ProcurementActionButton } from '../../components/shared/ProcurementActionButton';
import { useTheme } from '../../context/ThemeContext';
import { createOfferPageUi } from './create-offer-theme';
import {
  EUROASIANN_OFFER_LABEL,
  RFQ_PAYMENT_TERM_CUSTOM_VALUE,
  RFQ_TERMS_FIELDS,
  RFQ_TERMS_INITIAL,
  isPresetRfqPaymentTerm,
} from '@euroasiann/shared';
import { useDocumentIntelligence, type IdpAutofillPayload } from '@euroasiann/shared/idp';
import { CountryCodeSelect } from '../../components/ui/CountryCodeSelect';
import {
  applyContactPhoneAutofill,
  DEFAULT_CONTACT_PHONE_COUNTRY_CODE,
  formatContactPhone,
  hydrateContactPhoneFields,
  normalizeContactPhoneLocal,
} from '../../utils/contact-phone';
import { PageSkeleton } from '@euroasiann/shared/ui';

interface Vessel {
  _id: string;
  name: string;
  imoNumber?: string;
  exVesselName?: string;
}

interface Item {
  id: string;
  impaNo: string;
  itemDescription: string;
  partNo: string;
  altPartNo: string;
  drawingNumber: string;
  modelSerial?: string; // New field
  positionNo: string;
  dimensions: string;
  requiredQuantity: string;
  uom: string;
  quality: string;
  generalRemark: string;
  quotedPrice: string;
  expectedDeliveryDate?: string;
}

const QUALITY_OPTIONS = [
  'Genuine',
  'OEM',
  'Equivalent',
  'Copy Parts',
  'Mixed',
  'Brand as requested',
  'Custom manufacture',
  'Unknown',
  'Not-Applicable',
  'Compatible',
  'Substitute Brand',
] as const;

interface Brand {
  _id: string;
  name: string;
  status: string;
}

interface Category {
  _id: string;
  name: string;
  status: string;
}

interface Model {
  _id: string;
  name: string;
  brandId?: string;
  status: string;
}

function hydrateRfqDraftForm(savedFormData: Record<string, unknown> | undefined) {
  if (!savedFormData) return INITIAL_FORM_DATA;
  const phoneFields = hydrateContactPhoneFields(savedFormData);
  return {
    ...INITIAL_FORM_DATA,
    ...savedFormData,
    ...phoneFields,
  };
}

function offerMetadataItemsToFormItems(rawItems: Array<Record<string, unknown>>): Item[] {
  if (!rawItems.length) return INITIAL_ITEMS;
  return rawItems.map((item) => ({
    id: crypto.randomUUID(),
    impaNo: String(item.impaNo || ''),
    itemDescription: String(item.description || item.itemDescription || ''),
    partNo: String(item.partNo || ''),
    altPartNo: String(item.altPartNo || ''),
    drawingNumber: String(item.drawingNumber || ''),
    modelSerial: String(item.modelSerial || ''),
    positionNo: String(item.positionNo || ''),
    dimensions: String(item.dimensions || ''),
    requiredQuantity: String(item.requiredQty || item.offeredQty || item.quantity || ''),
    uom: String(item.uom || 'Pcs'),
    quality: String(item.offeredQuality || item.quality || ''),
    generalRemark: String(item.generalRemark || ''),
    quotedPrice: String(item.quotedPrice || item.unitPrice || ''),
    expectedDeliveryDate: String(item.expectedDeliveryDate || ''),
  }));
}

const INITIAL_FORM_DATA = {
  // Header - Core
  revisionNo: '00',
  currency: '',
  quoteDeadline: '',
  quoteValidityDays: '30',   // NEW � how many days quoted price is valid
  urgencyLevel: 'Normal', // New
  requisitionRef: '', // New

  // Header - Vessel & Equipment
  vesselName: '',
  imoNo: '',
  equipmentTags: '',
  category: '',
  subCategory: '',
  brand: '',
  model: '',
  department: '',
  remarks: '',

  typeOfIncoterms: '',
  typeOfLogisticContainer: '',
  ...RFQ_TERMS_INITIAL,
  leadDate: '',
  partialQuoteAllowed: 'no',
  splitOrderAllowed: 'no',

  // Offer recipient
  recipientEmail: '',
  recipientName: '',
  customerOrganizationId: '',
  customerOrganizationName: '',

  // Contact Person (NEW)
  contactName: '',
  contactEmail: '',
  contactPhoneCountryCode: DEFAULT_CONTACT_PHONE_COUNTRY_CODE,
  contactPhone: '',
  contactDepartment: 'Procurement',

  // Terms & Conditions (NEW)
  requiredPaymentTerms: '30 Days Credit',
  requiredCertificates: [] as string[],   // ['Class', 'OEM', 'COC', 'MTC']
  requiredDocuments: [] as string[],       // ['Commercial Invoice', 'Packing List', 'COO']
  packingInstructions: '',
  partialDeliveryAllowed: 'yes',
};


const CONTACT_DEPARTMENT_OPTIONS = ['Procurement', 'Technical', 'Operations'];

const TECHNICAL_DEPARTMENT_OPTIONS = ['Engine', 'Deck', 'Cabin', 'Operations', 'Safety', 'Other'];

const OFFER_IDP_TARGET_SCHEMA = {
  vesselName: 'Vessel name',
  imoNo: 'IMO number',
  requisitionRef: 'Requisition reference',
  category: 'Product category',
  brand: 'Equipment brand',
  model: 'Equipment model',
  contactName: 'Contact person',
  contactEmail: 'Contact email',
  contactPhone: 'Contact phone',
  currency: 'Currency',
  quoteDeadline: 'Quote deadline',
  items: 'Line items array',
};

const INITIAL_ITEMS: Item[] = [
  {
    id: '1',
    impaNo: '',
    itemDescription: '',
    partNo: '',
    altPartNo: '',
    drawingNumber: '',
    modelSerial: '',
    positionNo: '',
    dimensions: '',
    requiredQuantity: '',
    uom: '',
    quality: '',
    generalRemark: '',
    quotedPrice: '',
  },
];


export function CreateOfferPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editOfferId } = useParams<{ id?: string }>();
  const isEditMode = Boolean(editOfferId);
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const search = new URLSearchParams(location.search);
  const draftIdFromQuery = search.get('draftId') || '';
  const vesselIdFromQuery = search.get('vesselId') || '';
  const pmsJobNoFromQuery = search.get('pmsJobNo') || '';
  const pmsJobIdFromQuery = search.get('pmsJobId') || '';
  const pmsJobNoFromState =
    (location.state as { pmsJobNo?: string; pmsJobId?: string } | null)?.pmsJobNo || '';
  const pmsJobIdFromState =
    (location.state as { pmsJobNo?: string; pmsJobId?: string } | null)?.pmsJobId || '';
  const pmsJobNoRef = (pmsJobNoFromState || pmsJobNoFromQuery).trim();
  const pmsJobIdRef = (pmsJobIdFromState || pmsJobIdFromQuery).trim();

  const [formData, setFormData] = useState(() => {
    if (editOfferId) return INITIAL_FORM_DATA;
    const saved = localStorage.getItem('admin_offer_draft');
    if (saved) {
      try {
        const { formData: savedFormData } = JSON.parse(saved);
        return hydrateRfqDraftForm(savedFormData);
      } catch (e) {
        return INITIAL_FORM_DATA;
      }
    }
    return INITIAL_FORM_DATA;
  });

  const [paymentTermCustomMode, setPaymentTermCustomMode] = useState(() => {
    if (editOfferId) return false;
    try {
      const saved = localStorage.getItem('admin_offer_draft');
      if (!saved) return false;
      const { formData: savedFormData } = JSON.parse(saved);
      const t = String(savedFormData?.paymentTerm || '');
      return Boolean(t && !isPresetRfqPaymentTerm(t));
    } catch {
      return false;
    }
  });

  const [items, setItems] = useState<Item[]>(() => {
    if (editOfferId) return INITIAL_ITEMS;
    const saved = localStorage.getItem('admin_offer_draft');
    if (saved) {
      try {
        const { items: savedItems } = JSON.parse(saved);
        return savedItems || INITIAL_ITEMS;
      } catch (e) {
        return INITIAL_ITEMS;
      }
    }
    return INITIAL_ITEMS;
  });

  const [enableTaxConfig, setEnableTaxConfig] = useState<boolean>(() => {
    if (editOfferId) return false;
    try {
      const saved = localStorage.getItem('admin_offer_draft');
      if (!saved) return false;
      const parsed = JSON.parse(saved);
      return Boolean(parsed?.enableTaxConfig);
    } catch {
      return false;
    }
  });

  const [taxRate, setTaxRate] = useState<string>(() => {
    if (editOfferId) return '';
    try {
      const saved = localStorage.getItem('admin_offer_draft');
      if (!saved) return '';
      const parsed = JSON.parse(saved);
      return parsed?.taxRate != null ? String(parsed.taxRate) : '';
    } catch {
      return '';
    }
  });

  const [taxType, setTaxType] = useState<string>(() => {
    if (editOfferId) return '';
    try {
      const saved = localStorage.getItem('admin_offer_draft');
      if (!saved) return '';
      const parsed = JSON.parse(saved);
      return parsed?.taxType != null ? String(parsed.taxType) : '';
    } catch {
      return '';
    }
  });
  const [attachments, setAttachments] = useState<File[]>([]);

  const subtotal = useMemo(() => {
    return items.reduce((sum, item) => {
      const quantity = item.requiredQuantity ? parseInt(item.requiredQuantity.toString(), 10) || 0 : 0;
      const quotedPrice = item.quotedPrice ? parseFloat(item.quotedPrice.toString()) || 0 : 0;
      return sum + quantity * quotedPrice;
    }, 0);
  }, [items]);

  const numericTaxRate = useMemo(() => {
    if (!enableTaxConfig) return 0;
    const cleaned = taxRate.replace('%', '').trim();
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : Math.max(0, parsed);
  }, [enableTaxConfig, taxRate]);

  const taxAmount = useMemo(() => {
    if (!enableTaxConfig || numericTaxRate <= 0) return 0;
    return (subtotal * numericTaxRate) / 100;
  }, [enableTaxConfig, numericTaxRate, subtotal]);

  const finalTotal = useMemo(() => {
    return subtotal + taxAmount;
  }, [subtotal, taxAmount]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdRFQSummary, setCreatedRFQSummary] = useState<any>(null);
  const [savedRfqId, setSavedRfqId] = useState<string | null>(draftIdFromQuery || null);
  const [savedRfqNumber, setSavedRfqNumber] = useState<string | null>(null);
  const [hydratedEditOfferId, setHydratedEditOfferId] = useState<string | null>(null);
  const [showAddOrgModal, setShowAddOrgModal] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgError, setNewOrgError] = useState('');

  const { data: branding } = useQuery<{ name?: string; logoUrl?: string | null }>({
    queryKey: ['admin-offer-branding'],
    queryFn: async () => {
      const { fetchMyBranding } = await import('../../utils/document-letterhead');
      return fetchMyBranding('/api/v1/admin', authenticatedFetch);
    },
    staleTime: 300_000,
    refetchOnWindowFocus: false,
  });

  const excelInputRef = useRef<HTMLInputElement>(null);
  const [excelImportPreview, setExcelImportPreview] = useState<RfqExcelImportPreview | null>(null);
  const [excelImportFileName, setExcelImportFileName] = useState('');
  const [isImportProcessing, setIsImportProcessing] = useState(false);

  const {
    status: idpStatus,
    uploadFile: uploadIdpFile,
  } = useDocumentIntelligence({
    documentType: 'rfq',
    context: 'Admin offer creation — extract vessel, requisition, contact, and line items from uploaded documents',
    targetSchema: OFFER_IDP_TARGET_SCHEMA,
    fetchWithAuth: authenticatedFetch,
  });

  // Validation error tracking
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());

  // Helper: returns red-border + shake CSS when field has an error
  const errClass = (field: string) =>
    fieldErrors.has(field)
      ? ' !border-red-500 rfq-shake'
      : '';

  const {
    data: editOfferData,
    isLoading: isLoadingEditOffer,
    error: editOfferError,
  } = useQuery({
    queryKey: ['admin-offer-edit', editOfferId],
    enabled: isEditMode && Boolean(editOfferId),
    queryFn: async () => {
      const response = await authenticatedFetch(`/api/v1/admin/quotation/${editOfferId}`);
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || 'Failed to load offer');
      }
      return body.data?.offer || body.data;
    },
  });

  const { data: nextOfferNumberData } = useQuery({
    queryKey: ['admin-next-offer-number'],
    enabled: !isEditMode,
    staleTime: 60_000,
    queryFn: async () => {
      const response = await authenticatedFetch('/api/v1/admin/quotation/next-offer-number');
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || 'Failed to load next offer number');
      }
      return (body.data?.quotationNumber as string) || '';
    },
  });

  const previewOfferNumber =
    createdRFQSummary?.quotationNumber ||
    editOfferData?.quotationNumber ||
    nextOfferNumberData ||
    '';

  useEffect(() => {
    if (!isEditMode || !editOfferData || hydratedEditOfferId === editOfferData._id) return;

    const meta = editOfferData.metadata || {};
    const savedForm = meta.formData as Record<string, unknown> | undefined;
    const terms = (meta.terms || {}) as Record<string, string | undefined>;

    const hydrated = savedForm
      ? hydrateRfqDraftForm(savedForm)
      : {
          ...INITIAL_FORM_DATA,
          recipientEmail: meta.recipientEmail || '',
          recipientName: meta.recipientName || '',
          customerOrganizationId: meta.customerOrganizationId || '',
          revisionNo: meta.revisionNo || '00',
          currency: editOfferData.currency || terms.currency || '',
          vesselName: meta.vesselName || '',
          imoNo: meta.imoNumber || '',
          category: meta.category || '',
          subCategory: meta.subCategory || '',
          brand: meta.brand || '',
          model: meta.model || '',
          equipmentTags: meta.equipmentTags || '',
          department: meta.department || '',
          typeOfIncoterms: meta.typeOfIncoterms || '',
          typeOfLogisticContainer: meta.typeOfLogisticContainer || '',
          quoteDeadline: meta.quoteDeadline || '',
          quoteValidityDays: meta.quoteValidityDays || INITIAL_FORM_DATA.quoteValidityDays,
          urgencyLevel: meta.urgencyLevel || INITIAL_FORM_DATA.urgencyLevel,
          requisitionRef: meta.requisitionRef || '',
          contactName: meta.contactName || '',
          contactEmail: meta.contactEmail || '',
          requiredPaymentTerms: meta.requiredPaymentTerms || INITIAL_FORM_DATA.requiredPaymentTerms,
          remarks: editOfferData.description || '',
        };

    setFormData({
      ...hydrated,
      creditType: terms.creditType || hydrated.creditType,
      paymentTerm: terms.paymentTerm || hydrated.paymentTerm,
      insuranceTerm: terms.insuranceTerm || hydrated.insuranceTerm,
      taxTerm: terms.taxTerm || hydrated.taxTerm,
      transportTerm: terms.transportTerm || hydrated.transportTerm,
      deliveryTerm: terms.deliveryTerm || hydrated.deliveryTerm,
      packingTerm: terms.packingTerm || hydrated.packingTerm,
      recipientEmail: meta.recipientEmail || hydrated.recipientEmail,
      recipientName: meta.recipientName || hydrated.recipientName,
      customerOrganizationId: meta.customerOrganizationId || hydrated.customerOrganizationId,
    });
    {
      const pt = String(terms.paymentTerm || hydrated.paymentTerm || '');
      setPaymentTermCustomMode(Boolean(pt && !isPresetRfqPaymentTerm(pt)));
    }

    const rawItems = Array.isArray(meta.items) ? meta.items : editOfferData.items || [];
    setItems(offerMetadataItemsToFormItems(rawItems));

    const metaTax = (meta as any)?.taxConfig;
    if (metaTax && typeof metaTax === 'object') {
      setEnableTaxConfig(Boolean(metaTax.enabled));
      setTaxRate(metaTax.taxRate != null ? String(metaTax.taxRate) : '');
      setTaxType(metaTax.taxType != null ? String(metaTax.taxType) : '');
    } else if ((meta as any)?.taxRate != null) {
      setEnableTaxConfig(true);
      setTaxRate(String((meta as any).taxRate));
    }

    setSavedRfqNumber(meta.rfqNumber || null);
    setHydratedEditOfferId(editOfferData._id);
  }, [isEditMode, editOfferData, hydratedEditOfferId]);

  // Draft Persistence Logic
  useEffect(() => {
    if (isEditMode) return;
    const draft = JSON.stringify({ formData, items, enableTaxConfig, taxRate, taxType });
    localStorage.setItem('admin_offer_draft', draft);
  }, [formData, items, enableTaxConfig, taxRate, taxType, isEditMode]);

  useEffect(() => {
    if (!pmsJobNoRef) return;
    setFormData(prev => {
      if (prev.requisitionRef?.trim()) return prev;
      return {
        ...prev,
        requisitionRef: pmsJobNoRef,
      };
    });
  }, [pmsJobNoRef]);

  const [orgLookupStatus, setOrgLookupStatus] = useState<'idle' | 'loading' | 'found' | 'not_found'>('idle');

  useEffect(() => {
    const email = formData.recipientEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setOrgLookupStatus('idle');
      setFormData((prev) => ({
        ...prev,
        customerOrganizationId: '',
        customerOrganizationName: prev.customerOrganizationId ? '' : prev.customerOrganizationName,
      }));
      return;
    }

    setOrgLookupStatus('loading');
    const timer = window.setTimeout(async () => {
      try {
        const res = await authenticatedFetch(
          `/api/v1/admin/quotation/lookup-recipient?email=${encodeURIComponent(email)}`
        );
        const json = await res.json().catch(() => ({}));
        if (res.ok && json.success && json.data?.organizationId) {
          setOrgLookupStatus('found');
          setFormData((prev) => ({
            ...prev,
            customerOrganizationId: String(json.data.organizationId),
            customerOrganizationName: String(json.data.organizationName || ''),
          }));
        } else {
          setOrgLookupStatus('not_found');
          setFormData((prev) => ({
            ...prev,
            customerOrganizationId: '',
          }));
        }
      } catch {
        setOrgLookupStatus('not_found');
      }
    }, 400);

    return () => window.clearTimeout(timer);
  }, [formData.recipientEmail]);

  const { isLoading: isLoadingDraft } = useQuery({
    queryKey: ['admin-offer-draft', draftIdFromQuery],
    enabled: false,
    retry: false,
    queryFn: async () => {
      const response = await authenticatedFetch(`/api/v1/admin/rfq/${draftIdFromQuery}`);
      if (!response.ok) {
        throw new Error('Failed to load draft RFQ');
      }
      const body = await response.json();
      const rfq = body.data;
      if (String(rfq?.status || '').toLowerCase() !== 'draft') {
        throw new Error('Only draft RFQs can be edited here');
      }

      const meta = rfq.metadata || {};
      const vesselId = rfq.vesselId?._id || rfq.vesselId;
      setSavedRfqId(String(rfq._id));
      setSavedRfqNumber(rfq.rfqNumber || null);
      setFormData((prev) => ({
        ...prev,
        vesselName: vesselId ? String(vesselId) : rfq.vesselName || prev.vesselName,
        imoNo: rfq.imoNumber || meta.imoNumber || prev.imoNo,
        equipmentTags: rfq.equipmentTags || meta.equipmentTags || prev.equipmentTags,
        category: rfq.category || prev.category,
        subCategory: rfq.subCategory || meta.subCategory || prev.subCategory,
        brand: rfq.brand || prev.brand,
        model: rfq.model || rfq.equipmentModel || prev.model,
        department: rfq.department || meta.department || prev.department,
        remarks: rfq.remarks || meta.remarks || prev.remarks,
        requisitionRef: meta.requisitionRef || meta.pmsJobId || prev.requisitionRef,
        currency: meta.currency || prev.currency,
        quoteDeadline: meta.quoteDeadline || prev.quoteDeadline,
        contactName: meta.contactName != null ? String(meta.contactName) : prev.contactName,
        contactEmail: meta.contactEmail != null ? String(meta.contactEmail) : prev.contactEmail,
        ...hydrateContactPhoneFields({
          contactPhoneCountryCode: meta.contactPhoneCountryCode,
          contactPhone: meta.contactPhone ?? prev.contactPhone,
        }),
        contactDepartment:
          meta.contactDepartment != null ? String(meta.contactDepartment) : prev.contactDepartment,
      }));

      const sourceItems = Array.isArray(rfq.items) && rfq.items.length > 0
        ? rfq.items
        : Array.isArray(meta.items)
          ? meta.items
          : [];

      if (sourceItems.length > 0) {
        setItems(
          sourceItems.map((item: Record<string, unknown>, index: number) => ({
            id: String(item.id || index + 1),
            impaNo: String(item.impaNo || ''),
            itemDescription: String(item.itemDescription || ''),
            partNo: String(item.partNo || ''),
            altPartNo: String(item.altPartNo || ''),
            drawingNumber: String(item.drawingNumber || item.drawingNo || ''),
            modelSerial: String(item.modelSerial || ''),
            positionNo: String(item.positionNo || ''),
            dimensions: String(item.dimensions || ''),
            requiredQuantity: String(item.requiredQuantity ?? ''),
            uom: String(item.uom || ''),
            quality: String(item.quality || ''),
            generalRemark: String(item.generalRemark || ''),
            quotedPrice: String(item.quotedPrice ?? item.unitPrice ?? ''),
            expectedDeliveryDate: String(item.expectedDeliveryDate || ''),
          }))
        );
      }

      localStorage.removeItem('admin_offer_draft');
      return rfq;
    },
  });

  const { data: customerOrgs = [], refetch: refetchCustomerOrgs } = useQuery({
    queryKey: ['admin-customer-orgs-offer-create'],
    queryFn: async () => {
      const res = await authenticatedFetch('/api/v1/admin/organizations?portalType=customer&limit=200');
      if (!res.ok) return [];
      const json = await res.json();
      const raw = json.data?.organizations ?? json.data?.data ?? json.data ?? json.organizations ?? [];
      return Array.isArray(raw) ? raw.filter((o: any) => o.portalType === 'customer' || o.type === 'customer') : [];
    },
    staleTime: 60_000,
  });

  const createCustomerOrgMutation = useMutation({
    mutationFn: async (nameInput: string) => {
      const name = nameInput.trim();
      if (!name) throw new Error('Organization name is required');

      const response = await authenticatedFetch('/api/v1/admin/organizations', {
        method: 'POST',
        body: JSON.stringify({
          name,
          type: 'customer',
          portalType: 'customer',
          isActive: true,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || data?.message || 'Failed to create organization');
      }
      return data;
    },
    onSuccess: async (data) => {
      const org = data?.data?.organization || data?.data;
      const orgId = org?._id || org?.id;
      showToast('Organization added', 'success');
      setShowAddOrgModal(false);
      setNewOrgName('');
      setNewOrgError('');
      await queryClient.invalidateQueries({ queryKey: ['admin-customer-orgs-offer-create'] });
      await refetchCustomerOrgs();
      if (orgId) {
        setFormData((prev) => ({ ...prev, customerOrganizationId: String(orgId) }));
      }
    },
    onError: (error: Error) => {
      setNewOrgError(error.message);
      showToast(error.message, 'error');
    },
  });

  // Fetch vessels
  const { data: vesselsData } = useQuery<Vessel[]>({
    queryKey: ['admin-vessels'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/v1/admin/vessels');
      if (!response.ok) return [];
      const data = await response.json();
      const payload = data.data;
      if (Array.isArray(payload)) {
        return payload;
      }
      if (payload && Array.isArray(payload.data)) {
        return payload.data;
      }
      return [];
    },
    staleTime: Infinity, // Never consider data stale - prevent all refetching
    gcTime: Infinity, // Keep in cache forever
    refetchInterval: false, // Explicitly disable automatic refetching (false overrides global default)
    refetchOnWindowFocus: false, // Disable refetch on window focus
    refetchOnMount: false, // Disable refetch on component mount
    refetchOnReconnect: false, // Disable refetch on network reconnect
    retry: false, // Disable retry on failure to prevent continuous retry attempts
  });

  useEffect(() => {
    if (!vesselIdFromQuery || !vesselsData?.length) return;
    const vessel = vesselsData.find((v) => v._id === vesselIdFromQuery);
    if (!vessel) return;
    setFormData((prev) => {
      if (prev.vesselName === vesselIdFromQuery) return prev;
      return {
        ...prev,
        vesselName: vesselIdFromQuery,
        imoNo: vessel.imoNumber || prev.imoNo,
      };
    });
  }, [vesselIdFromQuery, vesselsData]);

  // Fetch categories from Parts Hierarchy (same source as Product Hierarchy page)
  const { data: categories } = useQuery<Category[]>({
    queryKey: ['admin-categories-active'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/v1/admin/categories?status=active&skipPopulate=true');
      if (!response.ok) return [];
      const data = await response.json();
      const payload = data.data;
      if (!Array.isArray(payload)) return [];
      return payload.map((cat: { _id: string; name: string }) => ({
        _id: String(cat._id),
        name: cat.name,
        status: 'active',
      }));
    },
    retry: false,
  });

  // Fetch brands (only active)
  const { data: brands } = useQuery<Brand[]>({
    queryKey: ['admin-brands', formData.category],
    queryFn: async () => {
      if (!formData.category) return [];
      const selectedCategory = categories?.find((c: Category) => c.name === formData.category);
      if (!selectedCategory) return [];
      const response = await authenticatedFetch(`/api/v1/admin/brands?status=active&categoryId=${selectedCategory._id}`);
      if (!response.ok) return [];
      const data = await response.json();
      const payload = data.data;
      if (Array.isArray(payload)) return payload;
      if (payload && Array.isArray(payload.data)) return payload.data;
      return [];
    },
    enabled: !!formData.category && !!categories && categories.length > 0,
    staleTime: 0,
    gcTime: Infinity,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const { data: subCategoriesResponse, isLoading: subCategoriesLoading } = useQuery({
    queryKey: ['admin-subcategories', formData.category],
    queryFn: async () => {
      if (!formData.category) return { success: true, data: [] };
      const selectedCategory = categories?.find((c: Category) => c.name === formData.category);
      if (!selectedCategory) return { success: true, data: [] };
      const response = await authenticatedFetch(`/api/v1/admin/product-hierarchy/subcategories?categoryId=${selectedCategory._id}`);
      if (!response.ok) throw new Error('Failed to fetch subcategories');
      return response.json();
    },
    enabled: !!formData.category && !!categories?.length,
  });
  const subCategories = subCategoriesResponse?.data || [];

  // Fetch models (only active, filtered by brand)
  const { data: models } = useQuery<Model[]>({
    queryKey: ['admin-models', formData.brand],
    queryFn: async () => {
      if (!formData.brand) return [];
      // Find brand ID from brand name
      const selectedBrand = brands?.find(b => b.name === formData.brand);
      if (!selectedBrand) return [];
      const response = await authenticatedFetch(`/api/v1/admin/models?brandId=${selectedBrand._id}`);
      if (!response.ok) return [];
      const data = await response.json();
      const payload = data.data;
      if (Array.isArray(payload)) return payload;
      if (payload && Array.isArray(payload.data)) return payload.data;
      return [];
    },
    enabled: !!formData.brand && formData.brand !== 'Unknown' && !!brands && brands.length > 0,
    staleTime: Infinity, // Never consider data stale - prevent all refetching
    gcTime: Infinity, // Keep in cache forever
    refetchInterval: false, // Explicitly disable automatic refetching (false overrides global default)
    refetchOnWindowFocus: false, // Disable refetch on window focus for better performance
    refetchOnMount: false, // Disable refetch on component mount
    refetchOnReconnect: false, // Disable refetch on network reconnect
    retry: false, // Disable retry on failure to prevent continuous retry attempts
  });

  // Add New Items Modals State
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showModelModal, setShowModelModal] = useState(false);
  const [newModelName, setNewModelName] = useState('');
  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
  const [newSubCategoryName, setNewSubCategoryName] = useState('');

  // Create brand mutation
  const createBrandMutation = useMutation({
    mutationFn: async (name: string) => {
      const selectedCategory = categories?.find((c: Category) => c.name === formData.category);
      if (!selectedCategory) {
        throw new Error('Please select a category first');
      }
      const response = await authenticatedFetch('/api/v1/admin/brands', {
        method: 'POST',
        body: JSON.stringify({ name, categoryId: selectedCategory._id }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create brand');
      }
      return response.json();
    },
    onSuccess: (res, name) => {
      const created = res?.data;
      const selectedName = created?.name || name;
      queryClient.setQueryData(['admin-brands', formData.category], (old: Brand[] | undefined) => {
        const list = Array.isArray(old) ? old : [];
        if (list.some((b) => b._id === created?._id || b.name === selectedName)) return list;
        return [...list, created || ({ _id: `temp-${selectedName}`, name: selectedName } as Brand)];
      });
      setFormData((prev) => ({
        ...prev,
        brand: selectedName,
        model: '',
        subCategory: '',
      }));
      setShowBrandModal(false);
      setNewBrandName('');
      queryClient.invalidateQueries({ queryKey: ['admin-brands'] });
      showToast('Brand created and selected', 'success');
    },
    onError: (error: any) => {
      showToast(error.message || 'Failed to create brand', 'error');
    },
  });

  // Create category mutation
  const createCategoryMutation = useMutation({
    mutationFn: async (name: string) => {
      const response = await authenticatedFetch('/api/v1/admin/categories', {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create category');
      }
      return response.json();
    },
    onSuccess: (res, name) => {
      const created = res?.data;
      const selectedName = created?.name || name;
      queryClient.setQueryData(['admin-categories-active'], (old: Category[] | undefined) => {
        const list = Array.isArray(old) ? old : [];
        if (list.some((c) => c._id === created?._id || c.name === selectedName)) return list;
        return [...list, created || ({ _id: `temp-${selectedName}`, name: selectedName } as Category)];
      });
      setFormData((prev) => ({
        ...prev,
        category: selectedName,
        brand: '',
        model: '',
        subCategory: '',
      }));
      setShowCategoryModal(false);
      setNewCategoryName('');
      queryClient.invalidateQueries({ queryKey: ['admin-categories-active'] });
      showToast('Category created and selected', 'success');
    },
    onError: (error: any) => {
      showToast(error.message || 'Failed to create category', 'error');
    },
  });

  // Create subcategory mutation
  const createSubCategoryMutation = useMutation({
    mutationFn: async (name: string) => {
      const selectedCategory = categories?.find((c: Category) => c.name === formData.category);
      if (!selectedCategory) {
        throw new Error('Please select a category first');
      }
      const response = await authenticatedFetch('/api/v1/admin/product-hierarchy/subcategories', {
        method: 'POST',
        body: JSON.stringify({ name, categoryId: selectedCategory._id }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create subcategory');
      }
      return response.json();
    },
    onSuccess: (res, name) => {
      const created = res?.data;
      const selectedName = created?.name || name;
      queryClient.setQueryData(['admin-subcategories', formData.category], (old: any) => {
        const list = Array.isArray(old?.data) ? old.data : Array.isArray(old) ? old : [];
        if (list.some((s: any) => s._id === created?._id || s.name === selectedName)) {
          return old?.data ? old : { success: true, data: list };
        }
        const next = [...list, created || { _id: `temp-${selectedName}`, name: selectedName }];
        return old?.data !== undefined || old?.success ? { ...(old || {}), success: true, data: next } : { success: true, data: next };
      });
      setFormData((prev) => ({
        ...prev,
        subCategory: selectedName,
      }));
      setShowSubCategoryModal(false);
      setNewSubCategoryName('');
      queryClient.invalidateQueries({ queryKey: ['admin-subcategories'] });
      showToast('SubCategory created and selected', 'success');
    },
    onError: (error: any) => {
      showToast(error.message || 'Failed to create subcategory', 'error');
    },
  });

  // Create model mutation
  const createModelMutation = useMutation({
    mutationFn: async (name: string) => {
      // Find brand ID from brand name
      const selectedBrand = brands?.find(b => b.name === formData.brand);
      if (!selectedBrand) {
        throw new Error('Please select a brand first');
      }
      const response = await authenticatedFetch('/api/v1/admin/models', {
        method: 'POST',
        body: JSON.stringify({ name, brandId: selectedBrand._id }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create model');
      }
      return response.json();
    },
    onSuccess: (res, name) => {
      const created = res?.data;
      const selectedName = created?.name || name;
      queryClient.setQueryData(['admin-models', formData.brand], (old: Model[] | undefined) => {
        const list = Array.isArray(old) ? old : [];
        if (list.some((m) => m._id === created?._id || m.name === selectedName)) return list;
        return [...list, created || ({ _id: `temp-${selectedName}`, name: selectedName } as Model)];
      });
      setFormData((prev) => ({
        ...prev,
        model: selectedName,
        subCategory: '',
      }));
      setShowModelModal(false);
      setNewModelName('');
      queryClient.invalidateQueries({ queryKey: ['admin-models'] });
      showToast('Model created and selected', 'success');
    },
    onError: (error: any) => {
      showToast(error.message || 'Failed to create model', 'error');
    },
  });

  const sendOfferMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const response = await authenticatedFetch('/api/v1/admin/quotation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || 'Failed to send offer');
      }
      return body;
    },
    onSuccess: (body) => {
      queryClient.invalidateQueries({ queryKey: ['admin-offers'] });
      queryClient.invalidateQueries({ queryKey: ['admin-pipeline-overview'] });
      queryClient.invalidateQueries({ queryKey: ['admin-next-offer-number'] });
      queryClient.invalidateQueries({ queryKey: ['admin-crm-contacts'] });
      if (body.warning) {
        showToast(`Offer saved, but email failed: ${body.warning}`, 'warning');
      }
      setCreatedRFQSummary(body.data);
      setShowSuccessModal(true);
      localStorage.removeItem('admin_offer_draft');
      showToast(body.message || 'Offer sent successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to send offer', 'error');
    },
  });

  const updateOfferMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const response = await authenticatedFetch(`/api/v1/admin/quotation/${editOfferId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || 'Failed to update offer');
      }
      return body;
    },
    onSuccess: (body) => {
      queryClient.invalidateQueries({ queryKey: ['admin-offers'] });
      queryClient.invalidateQueries({ queryKey: ['admin-pipeline-overview'] });
      queryClient.invalidateQueries({ queryKey: ['admin-crm-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['admin-offer', editOfferId] });
      queryClient.invalidateQueries({ queryKey: ['admin-offer-edit', editOfferId] });
      if (body.warning) {
        showToast(`Offer updated, but email failed: ${body.warning}`, 'warning');
      }
      setCreatedRFQSummary(body.data);
      setShowSuccessModal(true);
      showToast(body.message || 'Offer updated and resent successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update offer', 'error');
    },
  });

  const isSubmitting = sendOfferMutation.isPending || updateOfferMutation.isPending;

  const handleInputChange = (field: string, value: string | string[]) => {
    // Clear error for this field when user interacts
    setFieldErrors((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });

    setFormData((prev) => {
      const updates: any = { [field]: value };

      // Reset dependent fields when parent changes
      if (field === 'category') {
        updates.brand = '';
        updates.model = '';
        updates.subCategory = '';
      }
      if (field === 'brand') {
        updates.model = '';
      }
      return { ...prev, ...updates };
    });

    // Handle "Add New" option (kept separate as it involves side effects like modals)
    if (value === '__add_new_brand__') {
      setShowBrandModal(true);
      setFormData((prev) => ({ ...prev, [field]: '' }));
    } else if (value === '__add_new_category__') {
      setShowCategoryModal(true);
      setFormData((prev) => ({ ...prev, [field]: '' }));
    } else if (value === '__add_new_sub_category__') {
      setShowSubCategoryModal(true);
      setFormData((prev) => ({ ...prev, [field]: '' }));
    } else if (value === '__add_new_model__') {
      setShowModelModal(true);
      setFormData((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const applyCrmContact = useCallback((contact: OfferCrmContact) => {
    const phoneHydrated = hydrateContactPhoneFields({
      contactPhoneCountryCode: contact.contactPhoneCountryCode,
      contactPhone: contact.contactPhone,
    });
    setFormData((prev) => ({
      ...prev,
      recipientEmail: contact.recipientEmail?.toLowerCase() || prev.recipientEmail,
      recipientName: contact.recipientName || prev.recipientName,
      customerOrganizationId: contact.customerOrganizationId || prev.customerOrganizationId,
      customerOrganizationName: contact.customerOrganizationName || prev.customerOrganizationName,
      contactName: contact.contactName ? contact.contactName.toUpperCase() : prev.contactName,
      contactEmail: contact.contactEmail ? contact.contactEmail.toUpperCase() : prev.contactEmail,
      contactDepartment: contact.contactDepartment || prev.contactDepartment,
      contactPhoneCountryCode: phoneHydrated.contactPhoneCountryCode || prev.contactPhoneCountryCode,
      contactPhone: phoneHydrated.contactPhone || prev.contactPhone,
    }));
  }, []);

  const handleVesselChange = (nextVesselName: string, nextImoNo: string) => {
    setFieldErrors((prev) => {
      if (!prev.has('vesselName')) return prev;
      const next = new Set(prev);
      next.delete('vesselName');
      return next;
    });
    setFormData((prev) => ({
      ...prev,
      // Registry IDs stay as-is; free-text vessel names are stored uppercase
      vesselName: /^[a-f\d]{24}$/i.test(nextVesselName)
        ? nextVesselName
        : nextVesselName.toUpperCase(),
      imoNo: nextImoNo,
    }));
  };

  const handleItemChange = (id: string, field: keyof Item, value: string) => {
    // Clear per-item error on change
    const idx = items.findIndex(i => i.id === id);
    if (idx >= 0) {
      const errKey =
        field === 'itemDescription' ? `item_desc_${idx}` :
          field === 'requiredQuantity' ? `item_qty_${idx}` :
            field === 'uom' ? `item_uom_${idx}` : null;
      if (errKey) {
        setFieldErrors((prev) => {
          if (!prev.has(errKey)) return prev;
          const next = new Set(prev);
          next.delete(errKey);
          return next;
        });
      }
    }
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        impaNo: '',
        itemDescription: '',
        partNo: '',
        altPartNo: '',
        drawingNumber: '',
        positionNo: '',
        dimensions: '',
        requiredQuantity: '',
        uom: 'Pcs',
        quality: '',
        generalRemark: '',
        quotedPrice: '',
        expectedDeliveryDate: '',
      },
    ]);
  };

  const handleDeleteItem = (id: string) => {
    if (items.length > 1) {
      setItems((prev) => prev.filter((item) => item.id !== id));
    } else {
      showToast('At least one item is required', 'error');
    }
  };

  const buildOfferPayload = () => {
    const selectedVessel = vesselsData?.find((v: Vessel) => v._id === formData.vesselName);
    const referenceName = selectedVessel?.name || formData.vesselName || 'Equipment';
    const categoryInfo = formData.category ? ` - ${formData.category}` : '';
    const brandInfo = formData.brand ? ` (${formData.brand})` : '';
    const title = `${EUROASIANN_OFFER_LABEL} for ${referenceName}${categoryInfo}${brandInfo}`;

    const offerItems = items.map((item) => {
      const quantity = item.requiredQuantity ? parseInt(item.requiredQuantity.toString(), 10) || 0 : 0;
      const quotedPrice = item.quotedPrice ? parseFloat(item.quotedPrice.toString()) || 0 : 0;
      return {
        description: item.itemDescription,
        requiredQty: quantity,
        offeredQty: quantity,
        quotedPrice,
        offeredQuality: item.quality || undefined,
        impaNo: item.impaNo,
        partNo: item.partNo,
        altPartNo: item.altPartNo,
        drawingNumber: item.drawingNumber,
        positionNo: item.positionNo,
        dimensions: item.dimensions,
        uom: item.uom,
        generalRemark: item.generalRemark,
      };
    });

    const subtotal = offerItems.reduce(
      (sum, item) => sum + item.quotedPrice * (Number(item.offeredQty) || 0),
      0
    );
    const parsedTaxRate = enableTaxConfig ? parseFloat(taxRate.replace('%', '').trim()) || 0 : 0;
    const calculatedTaxAmount = enableTaxConfig && parsedTaxRate > 0 ? (subtotal * parsedTaxRate) / 100 : 0;
    const totalAmount = subtotal + calculatedTaxAmount;

    const terms = {
      creditType: formData.creditType || undefined,
      paymentTerm: formData.paymentTerm || undefined,
      insuranceTerm: formData.insuranceTerm || undefined,
      taxTerm:
        formData.taxTerm ||
        (enableTaxConfig && parsedTaxRate > 0
          ? [taxType.trim(), `${parsedTaxRate}% Tax Included`].filter(Boolean).join(' — ')
          : undefined),
      transportTerm: formData.transportTerm || undefined,
      deliveryTerm: formData.deliveryTerm || undefined,
      packingTerm: formData.packingTerm || undefined,
      currency: formData.currency || 'USD',
    };

    return {
      recipientEmail: formData.recipientEmail.trim().toLowerCase(),
      recipientName: formData.recipientName.trim() || undefined,
      customerOrganizationId: formData.customerOrganizationId.trim() || undefined,
      rfqId: savedRfqId || undefined,
      quotationNumber: previewOfferNumber || undefined,
      title,
      description: formData.remarks || title,
      status: 'submitted',
      totalAmount,
      currency: formData.currency || 'USD',
      items: offerItems,
      metadata: {
        terms,
        items: offerItems,
        subtotal,
        taxRate: enableTaxConfig ? parsedTaxRate : undefined,
        taxAmount: calculatedTaxAmount > 0 ? calculatedTaxAmount : undefined,
        taxConfig: {
          enabled: enableTaxConfig,
          taxType: enableTaxConfig && taxType.trim() ? taxType.trim() : undefined,
          taxRate: parsedTaxRate,
          taxAmount: calculatedTaxAmount,
          subtotal,
        },
        customerOrganizationName:
          formData.customerOrganizationId.trim()
            ? undefined
            : formData.customerOrganizationName.trim() || undefined,
        rfqNumber: savedRfqNumber || undefined,
        formData,
        quoteDeadline: formData.quoteDeadline,
        quoteValidityDays: formData.quoteValidityDays,
        urgencyLevel: formData.urgencyLevel,
        requisitionRef: formData.requisitionRef,
        revisionNo: formData.revisionNo,
        contactName: formData.contactName,
        contactEmail: formData.contactEmail,
        contactPhone: formatContactPhone(formData.contactPhoneCountryCode, formData.contactPhone),
        contactPhoneCountryCode: formData.contactPhoneCountryCode,
        contactDepartment: formData.contactDepartment,
        requiredPaymentTerms: formData.requiredPaymentTerms,
        vesselName: (selectedVessel?.name || formData.vesselName || '').toUpperCase() || undefined,
        imoNumber: formData.imoNo || selectedVessel?.imoNumber,
        category: formData.category,
        subCategory: formData.subCategory,
        brand: formData.brand,
        model: formData.model,
        equipmentTags: formData.equipmentTags,
        department: formData.department,
        typeOfIncoterms: formData.typeOfIncoterms,
        typeOfLogisticContainer: formData.typeOfLogisticContainer,
      },
    };
  };

  const validateDraftFields = () => {
    const hasContent =
      Boolean(formData.vesselName?.trim()) ||
      Boolean(formData.category?.trim()) ||
      Boolean(formData.remarks?.trim()) ||
      Boolean(formData.requisitionRef?.trim()) ||
      items.some(
        (item) =>
          item.itemDescription?.trim() ||
          item.partNo?.trim() ||
          item.impaNo?.trim()
      );

    if (!hasContent) {
      showToast('Add at least a vessel, category, item, or remarks before saving a draft', 'error');
      return false;
    }
    return true;
  };

  const validatePublishFields = () => {
    const errors = new Set<string>();

    const email = formData.recipientEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.add('recipientEmail');
    if (!formData.category) errors.add('category');
    if (!formData.brand) errors.add('brand');
    if (!formData.model) errors.add('model');

    items.forEach((item, idx) => {
      if (!item.itemDescription) errors.add(`item_desc_${idx}`);
      if (!item.requiredQuantity) errors.add(`item_qty_${idx}`);
      if (!item.uom) errors.add(`item_uom_${idx}`);
    });

    if (errors.size > 0) {
      setFieldErrors(errors);
      showToast(`Please fill in ${errors.size} required fields before sending`, 'error');
      return false;
    }

    setFieldErrors(new Set());
    return true;
  };

  const handleSaveDraft = () => {
    if (!validateDraftFields()) return;
    showToast('Draft saved locally on this device', 'success');
  };

  const handleDownloadExcelTemplate = () => {
    downloadRfqExcelTemplate({
      vessels: vesselsData ?? [],
      categories: categories ?? [],
      vendors: [],
      ports: [],
    });
    showToast('Excel template downloaded', 'success');
  };

  const handleApplyExcelImport = () => {
    if (!excelImportPreview || excelImportPreview.errors.length > 0) return;
    setFormData({
      ...INITIAL_FORM_DATA,
      ...excelImportPreview.formPatch,
      ...hydrateContactPhoneFields(excelImportPreview.formPatch as Record<string, unknown>),
      requisitionRef:
        excelImportPreview.formPatch.requisitionRef?.trim() ||
        pmsJobNoRef ||
        INITIAL_FORM_DATA.requisitionRef,
    });
    setItems(
      excelImportPreview.items.length > 0
        ? (excelImportPreview.items as unknown as Item[])
        : INITIAL_ITEMS
    );
    setFieldErrors(new Set());
    setExcelImportPreview(null);
    setExcelImportFileName('');
    showToast('Offer form populated from Excel', 'success');
  };

  const applyIdpAutofill = (payload: IdpAutofillPayload) => {
    setFormData((prev) => {
      const next = { ...prev };
      const apply = (key: keyof typeof prev, fieldKey: string) => {
        const val = payload.fields[fieldKey]?.value;
        if (val != null && String(val).trim()) {
          if (key === 'contactPhone') {
            applyContactPhoneAutofill(next as Record<string, unknown>, val);
            return;
          }
          (next as Record<string, unknown>)[key as string] = String(val);
        }
      };
      apply('vesselName', 'vesselName');
      apply('imoNo', 'imoNo');
      apply('requisitionRef', 'requisitionRef');
      apply('category', 'category');
      apply('subCategory', 'subCategory');
      apply('brand', 'brand');
      apply('model', 'model');
      apply('equipmentTags', 'equipmentTags');
      apply('currency', 'currency');
      apply('quoteDeadline', 'quoteDeadline');
      apply('quoteValidityDays', 'quoteValidityDays');
      apply('urgencyLevel', 'urgencyLevel');
      apply('department', 'department');
      apply('contactName', 'contactName');
      apply('contactEmail', 'contactEmail');
      apply('contactPhone', 'contactPhone');
      apply('contactDepartment', 'contactDepartment');
      RFQ_TERMS_FIELDS.forEach(({ key }) => apply(key as keyof typeof formData, key));
      apply('partialQuoteAllowed', 'partialQuoteAllowed');
      apply('splitOrderAllowed', 'splitOrderAllowed');
      apply('requiredPaymentTerms', 'requiredPaymentTerms');
      apply('partialDeliveryAllowed', 'partialDeliveryAllowed');
      apply('remarks', 'remarks');
      apply('leadDate', 'leadDate');
      apply('revisionNo', 'revisionNo');
      apply('contactEmail', 'email');
      apply('contactPhone', 'phone');
      apply('contactName', 'contactPerson');

      const vesselText = payload.fields.vesselName?.value ?? payload.fields.vessel?.value;
      if (vesselText && vesselsData?.length) {
        const needle = String(vesselText).toLowerCase().replace(/\s+/g, '');
        const matched = vesselsData.find((v) => {
          const name = v.name.toLowerCase().replace(/\s+/g, '');
          return name === needle || name.includes(needle) || needle.includes(name);
        });
        if (matched) {
          next.vesselName = matched._id;
          next.imoNo = matched.imoNumber || next.imoNo;
        }
      }

      return next;
    });

    const rawItems = payload.fields.items?.value;
    if (Array.isArray(rawItems) && rawItems.length > 0) {
      setItems(
        rawItems.map((item: Record<string, unknown>, index: number) => ({
          id: String(item.id || index + 1),
          impaNo: String(item.impaNo || ''),
          itemDescription: String(item.itemDescription || item.description || ''),
          partNo: String(item.partNo || ''),
          altPartNo: String(item.altPartNo || ''),
          drawingNumber: String(item.drawingNumber || item.drawingNo || ''),
          modelSerial: String(item.modelSerial || ''),
          positionNo: String(item.positionNo || index + 1),
          dimensions: String(item.dimensions || ''),
          requiredQuantity: String(item.requiredQuantity ?? item.qty ?? ''),
          uom: String(item.uom || ''),
          quality: String(item.quality || ''),
          generalRemark: String(item.generalRemark || item.remarks || ''),
          quotedPrice: String(item.quotedPrice ?? item.price ?? item.unitPrice ?? ''),
          expectedDeliveryDate: String(item.expectedDeliveryDate || ''),
        }))
      );
    }

    setFieldErrors(new Set());
    showToast('Document fields extracted — review before submitting', 'success');
  };

  const handleImportFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;

    const isExcel =
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      /\.xlsx?$/i.test(file.name);
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

    if (!isExcel && !isPdf) {
      showToast('Please upload an Excel (.xlsx) or PDF file', 'error');
      return;
    }

    if (isExcel) {
      try {
        const preview = await parseRfqExcelFile(file, {
          vessels: vesselsData ?? [],
          categories: categories ?? [],
          vendors: [],
          ports: [],
          portAgents: [],
        });
        setExcelImportFileName(file.name);
        setExcelImportPreview(preview);
      } catch {
        showToast('Failed to parse Excel file', 'error');
      }
      return;
    }

    setIsImportProcessing(true);
    try {
      const result = await uploadIdpFile(file);
      if (result) {
        applyIdpAutofill(result);
      }
    } catch {
      showToast('Failed to extract fields from PDF', 'error');
    } finally {
      setIsImportProcessing(false);
    }
  };

  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const ui = useMemo(() => createOfferPageUi(isDark), [isDark]);

  const offerPreviewData = useMemo((): AdminOfferLike => {
    const payload = buildOfferPayload();
    const selectedVessel = vesselsData?.find((v: Vessel) => v._id === formData.vesselName);
    const previewItems = items.map((item) => {
      const quantity = item.requiredQuantity ? parseInt(item.requiredQuantity.toString(), 10) || 0 : 0;
      const quotedPrice = item.quotedPrice ? parseFloat(item.quotedPrice.toString()) || 0 : 0;
      return {
        description: item.itemDescription,
        impaNo: item.impaNo,
        partNo: item.partNo,
        altPartNo: item.altPartNo,
        drawingNumber: item.drawingNumber,
        positionNo: item.positionNo,
        dimensions: item.dimensions,
        offeredQty: quantity,
        requiredQty: quantity,
        quotedPrice,
        uom: item.uom,
        quality: item.quality,
        generalRemark: item.generalRemark,
      };
    });

    return {
      quotationNumber: previewOfferNumber || undefined,
      title: payload.title,
      description: payload.description,
      totalAmount: payload.totalAmount,
      currency: payload.currency,
      createdAt: createdRFQSummary?.createdAt || editOfferData?.createdAt || new Date().toISOString(),
      status: createdRFQSummary || editOfferData ? 'sent' : 'draft',
      items: previewItems,
      metadata: {
        recipientEmail: payload.recipientEmail,
        recipientName: payload.recipientName,
        vesselName: (selectedVessel?.name || formData.vesselName || '').toUpperCase() || undefined,
        imoNumber: formData.imoNo || selectedVessel?.imoNumber,
        category: formData.category,
        subCategory: formData.subCategory,
        brand: formData.brand,
        model: formData.model,
        department: formData.department,
        contactName: formData.contactName,
        contactEmail: formData.contactEmail,
        contactPhone: formatContactPhone(formData.contactPhoneCountryCode, formData.contactPhone),
        contactDepartment: formData.contactDepartment,
        quoteDeadline: formData.quoteDeadline,
        quoteValidityDays: formData.quoteValidityDays,
        leadDate: formData.leadDate,
        remarks: formData.remarks,
        revisionNo: formData.revisionNo,
        requisitionRef: formData.requisitionRef,
        urgencyLevel: formData.urgencyLevel,
        equipmentTags: formData.equipmentTags,
        typeOfIncoterms: formData.typeOfIncoterms,
        typeOfLogisticContainer: formData.typeOfLogisticContainer,
        partialQuoteAllowed: formData.partialQuoteAllowed,
        splitOrderAllowed: formData.splitOrderAllowed,
        requiredPaymentTerms: formData.requiredPaymentTerms,
        requiredCertificates: formData.requiredCertificates,
        requiredDocuments: formData.requiredDocuments,
        packingInstructions: formData.packingInstructions,
        partialDeliveryAllowed: formData.partialDeliveryAllowed,
        companyLogoUrl: branding?.logoUrl || '/euroasiann-offer-logo.png',
        companyNameForLetterhead: branding?.name || 'Euroasiann Group',
        terms: payload.metadata.terms,
        items: previewItems,
        taxConfig: payload.metadata.taxConfig,
        taxRate: payload.metadata.taxRate,
        taxAmount: payload.metadata.taxAmount,
        subtotal: payload.metadata.subtotal,
      },
    };
  }, [formData, items, enableTaxConfig, taxRate, taxType, vesselsData, branding, createdRFQSummary, editOfferData, previewOfferNumber]);

  const executeSendOffer = async () => {
    const payload: Record<string, unknown> = buildOfferPayload();
    try {
      const pdfBlob = await buildAdminOfferPdfBlob(offerPreviewData);
      const offerPdfBase64 = await adminOfferPdfBlobToBase64(pdfBlob);
      payload.offerPdfBase64 = offerPdfBase64;
      payload.offerPdfFilename = `${EUROASIANN_OFFER_LABEL.replace(/\s+/g, '_')}_${
        offerPreviewData.quotationNumber || 'Offer'
      }.pdf`;
    } catch (err: any) {
      showToast(err?.message || 'Failed to generate offer PDF for email', 'error');
      return;
    }
    if (isEditMode && editOfferId) {
      updateOfferMutation.mutate(payload);
      return;
    }
    sendOfferMutation.mutate(payload);
  };

  const handleGenerateAndSend = () => {
    if (!validatePublishFields()) return;
    void executeSendOffer();
  };

  if (isEditMode && isLoadingEditOffer) {
    return <PageSkeleton />;
  }

  if (isEditMode && editOfferError) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600 font-semibold">{(editOfferError as Error).message || 'Failed to load offer'}</p>
        <button
          type="button"
          onClick={() => navigate('/dashboard/admin/offers')}
          className="mt-4 px-4 py-2 rounded-xl bg-[#0088cc] text-white font-bold"
        >
          Back to Offers
        </button>
      </div>
    );
  }

  if (isEditMode && editOfferData && !canEditAdminOffer(editOfferData)) {
    return (
      <div className="p-8 text-center max-w-lg mx-auto">
        <p className="text-amber-700 font-semibold">
          This offer can no longer be edited because it was accepted or already has a linked purchase order.
        </p>
        <button
          type="button"
          onClick={() => navigate(`/dashboard/admin/offers/${editOfferId}`)}
          className="mt-4 px-4 py-2 rounded-xl bg-[#0088cc] text-white font-bold"
        >
          View Offer
        </button>
      </div>
    );
  }

  return (
    <div className={ui.page}>
      {/* Shake animation for validation */}
      <style>{`
        @keyframes rfq-shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
        .rfq-shake {
          animation: rfq-shake 0.45s ease;
          border-color: #ef4444 !important;
          box-shadow: 0 0 0 2px rgba(239,68,68,0.25);
        }
      `}</style>
      {/* Breadcrumbs */}
      <div className="mb-5 flex items-center gap-2">
        <span className={cn('text-[10px] font-bold uppercase tracking-[0.2em]', ui.breadcrumbMuted)}>Admin</span>
        <span className={cn('text-[10px]', ui.breadcrumbSep)}>/</span>
        <span
          className={cn('cursor-pointer text-[10px] font-bold uppercase tracking-[0.2em] transition-colors', ui.breadcrumbLink)}
          onClick={() => navigate('/dashboard/admin/offers')}
        >
          Offers
        </span>
        <span className={cn('text-[10px]', ui.breadcrumbSep)}>/</span>
        <span className={cn('text-[10px] font-black uppercase tracking-[0.2em]', ui.breadcrumbActive)}>
          {isEditMode ? 'Edit Offer' : 'New Offer'}
        </span>
      </div>

      <section className={ui.hero}>
        <div className={ui.heroGradient} />
        <div className={ui.heroGlow} />
        <div className={ui.heroRibbon}>
          <span className={cn('text-[10px] font-bold uppercase tracking-[0.22em]', ui.heroRibbonText)}>
            Procurement Command · Offer Dispatch
          </span>
        </div>
        <div className="relative flex flex-col gap-6 p-6 sm:p-8 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0 space-y-3">
            <h1 className={ui.title}>{isEditMode ? 'Edit Offer' : 'Send New Offer'}</h1>
            <div className="flex flex-wrap items-center gap-3">
              <p className={ui.subtitle}>
                {isEditMode
                  ? 'Update the offer details — the recipient will receive a fresh email with accept/reject links'
                  : 'Configure and send a direct Euroasiann offer — no RFQ required'}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center xl:justify-end">
            <input
              type="file"
              ref={excelInputRef}
              accept=".xlsx,.xls,.pdf,application/pdf"
              onChange={handleImportFileSelected}
              className="hidden"
            />
            <ProcurementActionButton
              type="button"
              size="md"
              className={cn(ui.cmdBtn, 'min-w-[160px]')}
              onClick={handleDownloadExcelTemplate}
              icon={<MdDownload className="h-4 w-4" />}
            >
              Download Template
            </ProcurementActionButton>
            <ProcurementActionButton
              type="button"
              size="md"
              className={cn(ui.cmdBtn, 'min-w-[160px]')}
              onClick={() => excelInputRef.current?.click()}
              disabled={isImportProcessing || idpStatus === 'uploading' || idpStatus === 'processing'}
              icon={<MdFileUpload className="h-4 w-4" />}
            >
              {isImportProcessing || idpStatus === 'uploading' || idpStatus === 'processing'
                ? 'Extracting…'
                : 'Import Excel / PDF'}
            </ProcurementActionButton>
            <button
              type="button"
              onClick={() => navigate('/dashboard/admin/offers')}
              className={cn(ui.cmdBtn, 'min-w-[160px]', ui.abortBtn)}
            >
              Back to Offers
            </button>
          </div>
        </div>
      </section>

      {excelImportPreview && (
        <OfferExcelImportModal
          preview={excelImportPreview}
          fileName={excelImportFileName}
          onConfirm={handleApplyExcelImport}
          onCancel={() => {
            setExcelImportPreview(null);
            setExcelImportFileName('');
          }}
        />
      )}

      {/* RFQ and Vessel Information Section */}
      <div className={ui.section}>
        <div className={ui.sectionHeader}>
          <h2 className={ui.sectionTitle}>
            General Information
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <OfferCrmSuggestField
            label={
              <>
                Recipient Email <span className="text-red-500">*</span>
              </>
            }
            type="email"
            value={formData.recipientEmail}
            onChange={(v) => handleInputChange('recipientEmail', v)}
            onSelectContact={applyCrmContact}
            placeholder="customer@company.com"
            labelClassName={ui.label}
            inputClassName={cn(ui.field, errClass('recipientEmail'))}
          />
          <OfferCrmSuggestField
            label="Recipient Name"
            value={formData.recipientName}
            onChange={(v) => handleInputChange('recipientName', v)}
            onSelectContact={applyCrmContact}
            placeholder="Contact person"
            labelClassName={ui.label}
            inputClassName={ui.field}
          />
          <div className="space-y-1.5 md:col-span-2">
            <label className={ui.label}>
              Customer organization
              {orgLookupStatus === 'loading' && (
                <span className="text-[10px] font-normal text-[hsl(var(--muted-foreground))] ml-2">Looking up…</span>
              )}
              {orgLookupStatus === 'found' && formData.customerOrganizationId ? (
                <span className="text-[10px] font-normal text-emerald-600 ml-2">Linked to portal account</span>
              ) : null}
              {orgLookupStatus === 'not_found' ? (
                <span className="text-[10px] font-normal text-[hsl(var(--muted-foreground))] ml-2">Optional if recipient is not in ERP</span>
              ) : null}
            </label>
            {formData.customerOrganizationId ? (
              <input
                type="text"
                readOnly
                value={formData.customerOrganizationName || 'Linked organization'}
                className={cn(ui.field, 'bg-[hsl(var(--muted))]/40 cursor-not-allowed')}
              />
            ) : (
              <OfferCrmSuggestField
                hideLabel
                label="Customer organization"
                value={formData.customerOrganizationName}
                onChange={(v) => handleInputChange('customerOrganizationName', v)}
                onSelectContact={applyCrmContact}
                placeholder="Enter customer company name (optional)"
                inputClassName={ui.field}
                disabled={orgLookupStatus === 'loading'}
              />
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Organization is filled automatically when the recipient email matches a customer portal user. Otherwise
              type the company name manually — it is not required.
            </p>
          </div>
        </div>

        <div className={cn('mb-8 border-t pt-6', ui.divider)}>
          <h3 className={cn('mb-6 text-sm font-bold uppercase tracking-wider', ui.sectionTitle)}>
            Contact Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <OfferCrmSuggestField
              label="Contact Name"
              value={formData.contactName}
              onChange={(v) => handleInputChange('contactName', v)}
              onSelectContact={applyCrmContact}
              placeholder="PRIMARY CONTACT"
              labelClassName={ui.label}
              inputClassName={cn(ui.field, 'uppercase')}
              uppercase
            />
            <OfferCrmSuggestField
              label="Email Address"
              type="email"
              value={formData.contactEmail}
              onChange={(v) => handleInputChange('contactEmail', v)}
              onSelectContact={applyCrmContact}
              placeholder="CONTACT@COMPANY.COM"
              labelClassName={ui.label}
              inputClassName={cn(ui.field, 'uppercase')}
              uppercase
            />
            <div className="relative z-10 space-y-1.5">
              <label className={ui.label} htmlFor="offer-contact-phone">
                Phone / WhatsApp
              </label>
              <div className="flex gap-2">
                <CountryCodeSelect
                  id="offer-contact-phone-code"
                  value={formData.contactPhoneCountryCode}
                  onChange={(code) => handleInputChange('contactPhoneCountryCode', code)}
                  className="w-36 shrink-0"
                  isDark={isDark}
                />
                <input
                  id="offer-contact-phone"
                  name="contactPhone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  value={formData.contactPhone}
                  onChange={(e) =>
                    handleInputChange('contactPhone', normalizeContactPhoneLocal(e.target.value))
                  }
                  placeholder="000 000 0000"
                  className={cn(ui.field, 'flex-1 normal-case tabular-nums tracking-normal')}
                />
              </div>
            </div>
            <DepartmentCombobox
              id="offer-contact-department"
              value={formData.contactDepartment}
              onChange={(department) => handleInputChange('contactDepartment', department)}
              defaultOptions={CONTACT_DEPARTMENT_OPTIONS}
              storageKey="contact"
              labelClassName={ui.label}
              inputClassName={ui.field}
              isDark={isDark}
            />
          </div>
        </div>

        {/* Row 1: Core RFQ Info */}
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-6 mb-8">
          <div className="space-y-1.5">
            <label className={ui.label}>
              RFQ Number
            </label>
            <input
              type="text"
              value="AUTO-GENERATED"
              disabled
              className={ui.fieldDisabled}
            />
            {pmsJobNoRef ? (
              <p className="text-[10px] text-[#0088cc] font-semibold tracking-wide">
                PMS Reference: {pmsJobNoRef}
                {pmsJobIdRef ? ` (ID: ${pmsJobIdRef})` : ''}
              </p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <label className={ui.label}>
              Revision
            </label>
            <input
              type="text"
              value={formData.revisionNo}
              onChange={(e) => handleInputChange('revisionNo', e.target.value)}
              placeholder="00"
              className={ui.field}
            />
          </div>
          <div className="relative z-30 space-y-1.5">
            <label className={ui.label}>
              Currency
            </label>
            <CurrencyPicker
              variant="field"
              value={formData.currency}
              onChange={(code) => handleInputChange('currency', code)}
              placeholder="SELECT"
              className={cn(ui.fieldSelect, errClass('currency'))}
            />
          </div>
        </div>

        {/* Row 2: Vessel & Technical */}
        <div className="relative z-20 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-16 pb-8">
          <VesselNameCombobox
            vessels={vesselsData ?? []}
            vesselName={formData.vesselName}
            onVesselChange={handleVesselChange}
            labelClassName={ui.label}
            inputClassName={ui.field}
            errorClassName={errClass('vesselName')}
          />
          <div className="space-y-1.5">
            <label className={ui.label}>
              IMO Number
            </label>
            <input
              type="text"
              value={formData.imoNo}
              onChange={(e) => handleInputChange('imoNo', e.target.value)}
              readOnly={isRegistryVesselSelection(vesselsData ?? [], formData.vesselName)}
              placeholder={isRegistryVesselSelection(vesselsData ?? [], formData.vesselName) ? '' : 'ENTER IMO NUMBER'}
              className={
                isRegistryVesselSelection(vesselsData ?? [], formData.vesselName)
                  ? ui.fieldDisabledPlain
                  : ui.field
              }
            />
          </div>
          <div className="space-y-1.5">
            <label className={ui.label}>
              Validity
            </label>
            <select
              value={formData.quoteValidityDays}
              onChange={(e) => handleInputChange('quoteValidityDays', e.target.value)}
              className={ui.fieldSelect}
            >
              <option value="15">15 DAYS</option>
              <option value="30">30 DAYS</option>
              <option value="60">60 DAYS</option>
            </select>
          </div>
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="space-y-1.5 flex flex-col">
            <label className={ui.label}>
              Equipment Tags
            </label>
            <input
              type="text"
              value={formData.equipmentTags}
              onChange={(e) => handleInputChange('equipmentTags', e.target.value)}
              placeholder="E.G. MAIN ENGINE, PUMP"
              className={cn(ui.field, 'uppercase')}
            />
          </div>
        </div>
      </div>

      {/* Technical Specification Section */}
      <div className={ui.section}>
        <div className={ui.sectionHeader}>
          <h2 className={ui.sectionTitle}>
            Technical Specification
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
          <div className="space-y-1.5">
            <label className={ui.label}>
              Category <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              className={cn(ui.fieldSelect, errClass('category'))}
            >
              <option value="">SELECT CATEGORY</option>
              {categories?.map((cat) => (
                <option key={cat._id} value={cat.name}>
                  {cat.name.toUpperCase()}
                </option>
              ))}
              <option value="__add_new_category__" className="text-blue-600 font-bold">
                + ADD NEW CATEGORY
              </option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className={ui.label}>
              Sub Category
            </label>
            <select
              value={formData.subCategory}
              onChange={(e) => handleInputChange('subCategory', e.target.value)}
              disabled={!formData.category}
              className={cn(ui.fieldSelect, 'disabled:opacity-50 disabled:cursor-not-allowed', errClass('subCategory'))}
            >
              <option value="">{formData.category ? 'SELECT SUB-TYPE' : 'CHOOSE CATEGORY FIRST'}</option>
              {subCategories?.map((sub: any) => (
                <option key={sub._id} value={sub.name}>
                  {sub.name.toUpperCase()}
                </option>
              ))}
              {formData.category && (
                <option value="__add_new_sub_category__" className="text-blue-600 font-bold">
                  + ADD NEW SUB CATEGORY
                </option>
              )}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className={ui.label}>
              Brand <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.brand}
              onChange={(e) => handleInputChange('brand', e.target.value)}
              disabled={!formData.category}
              className={cn(ui.fieldSelect, 'disabled:opacity-50 disabled:cursor-not-allowed', errClass('brand'))}
            >
              <option value="">{formData.category ? 'SELECT BRAND' : 'CHOOSE CATEGORY FIRST'}</option>
              {formData.category && (
                <option value="Unknown">UNKNOWN</option>
              )}
              {brands?.map((brand) => (
                <option key={brand._id} value={brand.name}>
                  {brand.name.toUpperCase()}
                </option>
              ))}
              {formData.category && (
                <option value="__add_new_brand__" className="text-blue-600 font-bold">
                  + ADD NEW BRAND
                </option>
              )}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className={ui.label}>
              Model <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.model}
              onChange={(e) => handleInputChange('model', e.target.value)}
              disabled={!formData.brand}
              className={cn(ui.fieldSelect, 'disabled:opacity-50 disabled:cursor-not-allowed', errClass('model'))}
            >
              <option value="">{formData.brand ? 'SELECT MODEL' : 'CHOOSE BRAND FIRST'}</option>
              {formData.brand && (
                <option value="Unknown">UNKNOWN</option>
              )}
              {models?.map((model) => (
                <option key={model._id} value={model.name}>
                  {model.name.toUpperCase()}
                </option>
              ))}
              {formData.brand && formData.brand !== 'Unknown' && (
                <option value="__add_new_model__" className="text-blue-600 font-bold">
                  + ADD NEW MODEL
                </option>
              )}
            </select>
          </div>
        </div>

        {/* Department */}
        <div className={cn('grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6 pt-6 border-t', ui.divider)}>
          <DepartmentCombobox
            id="offer-technical-department"
            value={formData.department}
            onChange={(department) => handleInputChange('department', department)}
            defaultOptions={TECHNICAL_DEPARTMENT_OPTIONS}
            storageKey="technical"
            label="Department"
            labelClassName={ui.label}
            inputClassName={cn(ui.field, errClass('department'))}
            isDark={isDark}
          />
        </div>
      </div>

      {/* Add Attachments Section */}
      <div className={ui.section}>
        <div className={ui.sectionHeader}>
          <h2 className={ui.sectionTitle}>
            Supporting Documents
          </h2>
        </div>

        <div className={ui.dropzone}>
          <div className="mx-auto max-w-xs space-y-4">
            <div className={ui.dropzoneIcon}>
              <MdFileUpload className={cn('h-7 w-7', isDark ? 'text-[#38bdf8]' : 'text-[#0088cc]')} />
            </div>
            <div className="space-y-1">
              <p className={cn('text-sm font-bold uppercase tracking-tight', ui.dropzoneTitle)}>Drop files or click to upload</p>
              <p className={cn('text-[11px] font-bold uppercase tracking-widest', ui.dropzoneHint)}>PDF, DOCX, XLSX (MAX 10MB)</p>
            </div>
            <label className="cursor-pointer inline-block">
              <input
                type="file"
                multiple
                onChange={(e) => {
                  const newFiles = Array.from(e.target.files || []);
                  setAttachments(prev => [...prev, ...newFiles]);
                }}
                className="hidden"
              />
              <span className="rounded-lg border border-[#0088cc]/40 bg-[#0088cc] px-8 py-2.5 text-[11px] font-bold uppercase tracking-widest text-white shadow-md transition-all hover:bg-[#0077b3]">
                Choose Files
              </span>
            </label>
          </div>
        </div>

        {/* Selected Files List */}
        {attachments.length > 0 && (
          <div className="mt-8 space-y-3">
            <h3 className={cn('text-[10px] font-black uppercase tracking-[0.2em] mb-4', ui.label)}>Selected for Upload ({attachments.length})</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {attachments.map((file, idx) => (
                <div key={`${file.name}-${idx}`} className={ui.attachmentCard}>
                  <div className="flex min-w-0 items-center gap-4">
                    <div className={ui.attachmentIcon}>
                      <MdInsertDriveFile className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className={cn('truncate text-[13px] font-bold uppercase tracking-tight', ui.attachmentName)} title={file.name}>{file.name}</p>
                      <p className={cn('text-[10px] font-bold uppercase', ui.dropzoneHint)}>{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                    className={ui.deleteRowBtn}
                    title="Remove File"
                  >
                    <MdDelete className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Items Section */}
      <div className={ui.section}>
        <div className={ui.sectionHeader}>
          <h2 className={ui.sectionTitle}>
            Required Items
          </h2>
        </div>
        <div className="overflow-x-auto -mx-8">
          <table className="w-full text-left border-collapse min-w-[1450px]">
            <thead>
              <tr className={ui.tableHead}>
                <th className="px-6 py-4 w-12 text-center">#</th>
                <th className="px-3 py-4 w-28">IMPA</th>
                <th className="px-3 py-4 flex-1">Description / Specification</th>
                <th className="px-3 py-4 w-36">Part No</th>
                <th className="px-3 py-4 w-32">Drawing No</th>
                <th className="px-3 py-4 w-32">Dim.</th>
                <th className="px-3 py-4 w-24">Qty</th>
                <th className="px-3 py-4 w-24">UoM</th>
                <th className="px-3 py-4 w-32">Quality</th>
                <th className="px-3 py-4 w-28">
                  Unit Price{formData.currency ? ` (${displayCurrencyMark(formData.currency)})` : ''}
                </th>
                <th className="px-3 py-4 w-48">Remarks</th>
                <th className="px-6 py-4 w-16 text-center">Action</th>
              </tr>
            </thead>
            <tbody className={ui.tableBody}>
              {items.map((item, index) => (
                <tr key={item.id} className={erpTableRowClasses()}>
                  <td className={cn('px-6 py-4 text-center text-[12px] font-bold', ui.tableRowNum)}>
                    {index + 1}
                  </td>
                  <td className="px-3 py-4">
                    <input
                      type="text"
                      value={item.impaNo}
                      onChange={(e) => handleItemChange(item.id, 'impaNo', e.target.value)}
                      className={ui.tableField}
                      placeholder="IMPA"
                    />
                  </td>
                  <td className="px-3 py-4">
                    <textarea
                      value={item.itemDescription}
                      onChange={(e) => handleItemChange(item.id, 'itemDescription', e.target.value)}
                      className={cn(ui.tableFieldTextarea, 'uppercase', errClass(`item_desc_${index}`))}
                      placeholder="DESCRIPTION *"
                    />
                  </td>
                  <td className="px-3 py-4">
                    <div className="flex flex-col gap-1">
                      <input
                        type="text"
                        value={item.partNo}
                        onChange={(e) => handleItemChange(item.id, 'partNo', e.target.value)}
                        className={ui.tableField}
                        placeholder="PART NO"
                      />
                      <input
                        type="text"
                        value={item.altPartNo}
                        onChange={(e) => handleItemChange(item.id, 'altPartNo', e.target.value)}
                        className={cn(ui.tableField, 'py-1 text-[11px] normal-case tracking-normal')}
                        placeholder="alt part no"
                      />
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <input
                      type="text"
                      value={item.drawingNumber}
                      onChange={(e) => handleItemChange(item.id, 'drawingNumber', e.target.value)}
                      className={ui.tableField}
                      placeholder="DRAWING NO"
                    />
                  </td>
                  <td className="px-3 py-4">
                    <input
                      type="text"
                      value={item.dimensions}
                      onChange={(e) => handleItemChange(item.id, 'dimensions', e.target.value)}
                      className={ui.tableField}
                      placeholder="DIMENSIONS"
                    />
                  </td>
                  <td className="px-3 py-4">
                    <input
                      type="number"
                      value={item.requiredQuantity}
                      onChange={(e) => handleItemChange(item.id, 'requiredQuantity', e.target.value)}
                      className={cn(ui.tableFieldCenter, errClass(`item_qty_${index}`))}
                      min="1"
                      placeholder="QTY"
                    />
                  </td>
                  <td className="px-3 py-4">
                    <select
                      value={item.uom}
                      onChange={(e) => handleItemChange(item.id, 'uom', e.target.value)}
                      className={cn(ui.tableSelect, errClass(`item_uom_${index}`))}
                    >
                      <option value="">UOM</option>
                      <option value="Pcs">PCS</option>
                      <option value="Kgs">KGS</option>
                      <option value="Ltrs">LTRS</option>
                      <option value="Mtrs">MTRS</option>
                      <option value="Sets">SETS</option>
                    </select>
                  </td>
                  <td className="px-3 py-4">
                    <select
                      value={item.quality}
                      onChange={(e) => handleItemChange(item.id, 'quality', e.target.value)}
                      className={ui.tableSelect}
                    >
                      <option value="">QUALITY</option>
                      {QUALITY_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option.toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-4">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.quotedPrice}
                      onChange={(e) => handleItemChange(item.id, 'quotedPrice', e.target.value)}
                      className={cn(ui.tableFieldCenter, 'normal-case tabular-nums')}
                      placeholder="UNIT PRICE"
                    />
                  </td>
                  <td className="px-3 py-4">
                    <textarea
                      value={item.generalRemark}
                      onChange={(e) => handleItemChange(item.id, 'generalRemark', e.target.value)}
                      className={cn(ui.tableFieldTextarea, 'uppercase', isDark ? 'text-[#0088cc]/65' : 'text-[hsl(var(--muted-foreground))]')}
                      placeholder="REMARKS"
                    />
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => setItems(items.filter((i) => i.id !== item.id))}
                      className={ui.deleteRowBtn}
                    >
                      <MdDeleteOutline className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={ui.itemsFooter}>
          <p className={cn('text-[12px] font-bold uppercase tracking-widest', ui.itemsFooterText)}>
            {items.length} {items.length === 1 ? 'ITEM' : 'ITEMS'} IN RFQ MANIFEST
          </p>
          <button
            onClick={handleAddItem}
            className={ui.addItemBtn}
          >
            + Add New Item
          </button>
        </div>

        {/* Pricing Summary & Tax Config */}
        <div className="mt-6 flex flex-col items-end border-t border-[#0088cc]/20 pt-6 dark:border-white/[0.08]">
          <div className={cn(
            'w-full max-w-md rounded-xl border p-5 shadow-sm space-y-4',
            isDark ? 'border-white/[0.08] bg-[#0f172a]/70' : 'border-[#0088cc]/20 bg-[#0088cc]/5'
          )}>
            {/* Total Price */}
            <div className="flex items-center justify-between gap-4">
              <span className={cn('text-xs font-bold uppercase tracking-wider', isDark ? 'text-slate-300' : 'text-slate-600')}>
                Total Price:
              </span>
              <span className={cn('text-base font-extrabold tabular-nums', isDark ? 'text-white' : 'text-slate-900')}>
                {formData.currency || 'USD'} {subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>

            {/* Checkbox named Tax Config */}
            <div className="flex items-center justify-between border-t pt-3 border-dashed border-[#0088cc]/20 dark:border-white/10">
              <label
                htmlFor="tax-config-checkbox"
                className="flex items-center gap-2.5 cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  id="tax-config-checkbox"
                  checked={enableTaxConfig}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setEnableTaxConfig(checked);
                    if (!checked) {
                      setTaxRate('');
                      setTaxType('');
                    }
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-[#0088cc] focus:ring-[#0088cc]/30 cursor-pointer accent-[#0088cc]"
                />
                <span className={cn('text-xs font-bold uppercase tracking-wider', isDark ? 'text-white' : 'text-slate-800')}>
                  Tax Config
                </span>
              </label>

              {enableTaxConfig && (
                <span className="text-[11px] font-semibold text-[#0088cc] dark:text-[#38bdf8]">
                  Tax enabled
                </span>
              )}
            </div>

            {/* Text input box opens just below the total price */}
            {enableTaxConfig && (
              <div className="space-y-3 pt-1">
                <div className="space-y-1.5">
                  <label className={ui.label}>Tax type</label>
                  <input
                    type="text"
                    value={taxType}
                    onChange={(e) => setTaxType(e.target.value)}
                    placeholder="e.g. GST, VAT, Sales tax"
                    className={cn(ui.field, 'text-sm')}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className={ui.label}>
                    Tax Rate (%)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={taxRate}
                      onChange={(e) => setTaxRate(e.target.value)}
                      placeholder="Enter tax percentage (e.g. 18)"
                      className={cn(ui.field, 'pr-8 font-mono text-sm')}
                      autoFocus
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                      %
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs font-medium">
                  <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                    Tax Amount ({numericTaxRate}%):
                  </span>
                  <span className={cn('font-bold tabular-nums', isDark ? 'text-[#38bdf8]' : 'text-[#0088cc]')}>
                    {formData.currency || 'USD'} {taxAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}

            {/* Grand Total */}
            <div className={cn(
              'flex items-center justify-between border-t pt-3.5',
              isDark ? 'border-white/10' : 'border-[#0088cc]/20'
            )}>
              <div>
                <div className={cn('text-xs font-extrabold uppercase tracking-wider', isDark ? 'text-white' : 'text-slate-900')}>
                  Final Total Offer:
                </div>
                {enableTaxConfig && numericTaxRate > 0 && (
                  <div className="text-[10px] text-slate-400">
                    Includes {[taxType.trim(), `${numericTaxRate}% tax`].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
              <span className={cn(
                'text-lg font-black tabular-nums',
                isDark ? 'text-white' : 'text-slate-900'
              )}>
                {formData.currency || 'USD'} {finalTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Terms & Conditions */}
      <div className={cn(ui.section, 'relative z-10 mt-8')}>
        <div className={cn(ui.sectionHeader, 'flex-wrap gap-3')}>
          <h2 className={ui.sectionTitle}>Terms & Conditions</h2>
          <a
            href={OFFER_SALE_TERMS_ROUTE}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-xs font-semibold text-[#0088cc] underline underline-offset-2 hover:text-[#0077b3]"
          >
            View full Euroasiann Offer Sale Terms
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {RFQ_TERMS_FIELDS.map(({ key, label, options }) => {
            if (key === 'paymentTerm') {
              const current = String(formData.paymentTerm || '');
              const selectVal = paymentTermCustomMode
                ? RFQ_PAYMENT_TERM_CUSTOM_VALUE
                : current && isPresetRfqPaymentTerm(current)
                  ? current
                  : current
                    ? RFQ_PAYMENT_TERM_CUSTOM_VALUE
                    : '';
              return (
                <div key={key} className="space-y-1.5">
                  <label className={ui.label}>{label}</label>
                  <select
                    value={selectVal}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next === RFQ_PAYMENT_TERM_CUSTOM_VALUE) {
                        setPaymentTermCustomMode(true);
                        if (isPresetRfqPaymentTerm(current)) {
                          handleInputChange(key, '');
                        }
                      } else {
                        setPaymentTermCustomMode(false);
                        handleInputChange(key, next);
                      }
                    }}
                    className={ui.fieldSelect}
                  >
                    <option value="">SELECT {label.toUpperCase()}</option>
                    {options.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                    <option value={RFQ_PAYMENT_TERM_CUSTOM_VALUE}>Custom (enter your own)</option>
                  </select>
                  {selectVal === RFQ_PAYMENT_TERM_CUSTOM_VALUE && (
                    <input
                      type="text"
                      value={current}
                      onChange={(e) => handleInputChange(key, e.target.value)}
                      placeholder="e.g. 40% advance, 60% against B/L"
                      className={ui.field}
                      autoFocus
                    />
                  )}
                </div>
              );
            }

            return (
              <div key={key} className="space-y-1.5">
                <label className={ui.label}>{label}</label>
                <select
                  value={formData[key as keyof typeof formData] as string}
                  onChange={(e) => handleInputChange(key, e.target.value)}
                  className={ui.fieldSelect}
                >
                  <option value="">SELECT {label.toUpperCase()}</option>
                  {options.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      </div>

      {/* Save Draft / Generate & Send */}
      <div className="mt-12 mb-12 space-y-3">
        <p className={cn('text-xs leading-relaxed', isDark ? 'text-slate-400' : 'text-[hsl(var(--muted-foreground))]')}>
          {isEditMode
            ? 'Saving will update the offer and send a new email to the recipient with updated accept/reject links.'
            : 'The recipient will receive an email with the offer summary and buttons to accept (with shipping choice) or reject the offer.'}
        </p>
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-4">
        {!isEditMode && (
        <button
          type="button"
          onClick={handleSaveDraft}
          disabled={isSubmitting || isLoadingDraft}
          className={cn(ui.cmdBtn, 'min-w-[160px] disabled:opacity-50', ui.saveDraftBtn)}
        >
          {isSubmitting ? 'Saving...' : 'Save Draft'}
        </button>
        )}
        <button
          type="button"
          onClick={handleGenerateAndSend}
          disabled={isSubmitting || isLoadingDraft}
          className={cn(ui.cmdBtn, 'min-w-[200px] disabled:opacity-50', ui.sendBtn)}
        >
          {isSubmitting
            ? 'Sending...'
            : isEditMode
              ? 'Save & Resend Offer'
              : `Send ${EUROASIANN_OFFER_LABEL}`}
        </button>
        </div>
      </div>

      <div className="mb-20">
        <OfferPdfPreviewSection offer={offerPreviewData} ui={ui} isDark={isDark} />
      </div>

      {/* Add Customer Organization Modal — name only */}
      {showAddOrgModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-md">
          <div className="w-full max-w-md rounded-lg bg-white p-6 dark:bg-[#0088cc]/18">
            <h3 className="mb-4 text-lg font-semibold text-[hsl(var(--foreground))] dark:text-white">
              Add Organization
            </h3>
            <input
              type="text"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newOrgName.trim() && !createCustomerOrgMutation.isPending) {
                  e.preventDefault();
                  createCustomerOrgMutation.mutate(newOrgName.trim());
                }
              }}
              placeholder="Enter organization name"
              autoFocus
              className="mb-3 w-full rounded-lg border border-[#0088cc]/25 bg-white px-4 py-2 text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-[#0088cc]/25 dark:bg-[#0088cc]/20 dark:text-white"
            />
            {newOrgError && (
              <p className="mb-3 text-sm font-semibold text-red-600">{newOrgError}</p>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowAddOrgModal(false);
                  setNewOrgName('');
                  setNewOrgError('');
                }}
                className="rounded-lg border border-[#0088cc]/25 px-4 py-2 text-[hsl(var(--foreground))] hover:bg-[#0088cc]/5 dark:border-[#0088cc]/25 dark:text-[#0088cc]/45 dark:hover:bg-[#0088cc]/20"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!newOrgName.trim() || createCustomerOrgMutation.isPending}
                onClick={() => createCustomerOrgMutation.mutate(newOrgName.trim())}
                className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {createCustomerOrgMutation.isPending ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Brand Modal */}
      {
        showBrandModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50">
            <div className="bg-white dark:bg-[#0088cc]/18 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] dark:text-white mb-4">Add New Brand</h3>
              <input
                type="text"
                value={newBrandName}
                onChange={(e) => setNewBrandName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newBrandName.trim() && !createBrandMutation.isPending) {
                    e.preventDefault();
                    createBrandMutation.mutate(newBrandName.trim());
                  }
                }}
                placeholder="Enter brand name"
                autoFocus
                className="w-full px-4 py-2 border border-[#0088cc]/25 dark:border-[#0088cc]/25 rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowBrandModal(false);
                    setNewBrandName('');
                  }}
                  className="px-4 py-2 border border-[#0088cc]/25 dark:border-[#0088cc]/25 rounded-lg text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 hover:bg-[#0088cc]/5 dark:hover:bg-[#0088cc]/20"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (newBrandName.trim()) {
                      createBrandMutation.mutate(newBrandName.trim());
                    }
                  }}
                  disabled={!newBrandName.trim() || createBrandMutation.isPending}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createBrandMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Add Category Modal */}
      {
        showCategoryModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50">
            <div className="bg-white dark:bg-[#0088cc]/18 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] dark:text-white mb-4">Add New Category</h3>
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newCategoryName.trim() && !createCategoryMutation.isPending) {
                    e.preventDefault();
                    createCategoryMutation.mutate(newCategoryName.trim());
                  }
                }}
                placeholder="Enter category name"
                autoFocus
                className="w-full px-4 py-2 border border-[#0088cc]/25 dark:border-[#0088cc]/25 rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowCategoryModal(false);
                    setNewCategoryName('');
                  }}
                  className="px-4 py-2 border border-[#0088cc]/25 dark:border-[#0088cc]/25 rounded-lg text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 hover:bg-[#0088cc]/5 dark:hover:bg-[#0088cc]/20"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (newCategoryName.trim()) {
                      createCategoryMutation.mutate(newCategoryName.trim());
                    }
                  }}
                  disabled={!newCategoryName.trim() || createCategoryMutation.isPending}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createCategoryMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Add Sub Category Modal */}
      {
        showSubCategoryModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50">
            <div className="bg-white dark:bg-[#0088cc]/18 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] dark:text-white mb-4">Add New Sub Category</h3>
              <input
                type="text"
                value={newSubCategoryName}
                onChange={(e) => setNewSubCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === 'Enter' &&
                    newSubCategoryName.trim() &&
                    formData.category &&
                    !createSubCategoryMutation.isPending
                  ) {
                    e.preventDefault();
                    createSubCategoryMutation.mutate(newSubCategoryName.trim());
                  }
                }}
                placeholder="Enter sub category name"
                autoFocus
                className="w-full px-4 py-2 border border-[#0088cc]/25 dark:border-[#0088cc]/25 rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              />
              {!formData.category && (
                <p className="text-sm text-red-500 mb-4">Please select a category first.</p>
              )}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowSubCategoryModal(false);
                    setNewSubCategoryName('');
                  }}
                  className="px-4 py-2 border border-[#0088cc]/25 dark:border-[#0088cc]/25 rounded-lg text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 hover:bg-[#0088cc]/5 dark:hover:bg-[#0088cc]/20"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (newSubCategoryName.trim()) {
                      createSubCategoryMutation.mutate(newSubCategoryName.trim());
                    }
                  }}
                  disabled={!newSubCategoryName.trim() || !formData.category || createSubCategoryMutation.isPending}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createSubCategoryMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Add Model Modal */}
      {
        showModelModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50">
            <div className="bg-white dark:bg-[#0088cc]/18 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] dark:text-white mb-4">Add New Model</h3>
              <input
                type="text"
                value={newModelName}
                onChange={(e) => setNewModelName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newModelName.trim() && !createModelMutation.isPending) {
                    e.preventDefault();
                    createModelMutation.mutate(newModelName.trim());
                  }
                }}
                placeholder="Enter model name"
                autoFocus
                className="w-full px-4 py-2 border border-[#0088cc]/25 dark:border-[#0088cc]/25 rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowModelModal(false);
                    setNewModelName('');
                  }}
                  className="px-4 py-2 border border-[#0088cc]/25 dark:border-[#0088cc]/25 rounded-lg text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 hover:bg-[#0088cc]/5 dark:hover:bg-[#0088cc]/20"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (newModelName.trim()) {
                      createModelMutation.mutate(newModelName.trim());
                    }
                  }}
                  disabled={!newModelName.trim() || createModelMutation.isPending}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createModelMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Success Modal */}
      {
        showSuccessModal && createdRFQSummary && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-white dark:bg-[#0088cc]/25 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-300">
              <div className="bg-blue-600 p-8 text-center text-white">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MdCheckCircle className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">
                  {isEditMode ? `${EUROASIANN_OFFER_LABEL} Updated & Resent!` : `${EUROASIANN_OFFER_LABEL} Sent Successfully!`}
                </h3>
                <p className="text-blue-100 opacity-90">
                  {isEditMode
                    ? 'The updated offer has been sent to the recipient.'
                    : 'Your offer has been sent to the recipient.'}
                </p>
              </div>

              <div className="p-8">
                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center pb-3 border-b border-[#0088cc]/15 dark:border-[#0088cc]/20">
                    <span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Offer Number</span>
                    <span className="font-semibold text-[hsl(var(--foreground))] dark:text-white">{createdRFQSummary.quotationNumber || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-[#0088cc]/15 dark:border-[#0088cc]/20">
                    <span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Vessel</span>
                    <span className="font-semibold text-[hsl(var(--foreground))] dark:text-white">
                      {getVesselDisplayName(vesselsData ?? [], formData.vesselName) || createdRFQSummary.vesselName || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-[#0088cc]/15 dark:border-[#0088cc]/20">
                    <span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Items</span>
                    <span className="font-semibold text-[hsl(var(--foreground))] dark:text-white">{items.length} items</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Recipient</span>
                    <span className="font-semibold text-[hsl(var(--foreground))] dark:text-white">{formData.recipientEmail || '-'}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={async () => {
                      await downloadAdminOfferPDF(offerPreviewData);
                    }}
                    className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#0088cc]/20 dark:border-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))] rounded-xl hover:bg-[#0088cc]/5 dark:hover:bg-[#0088cc]/18 transition-all font-semibold"
                  >
                    <MdDownload className="w-5 h-5" />
                    Download Offer
                  </button>
                  <button
                    onClick={() => navigate(isEditMode && editOfferId ? `/dashboard/admin/offers/${editOfferId}` : '/dashboard/admin/offers')}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-semibold shadow-lg shadow-blue-500/20"
                  >
                    Done
                    <MdArrowForward className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

    </div >
  );
}

export default CreateOfferPage;
